/* External */
import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { FormattedMessage, useIntl } from 'react-intl';

/* Ott-common */
import CommonCardStationWithSchedule from '@dtvgo/card-station-with-schedule';
import Icon from '@dtvgo/icon';
import { formatDate } from '@dtvgo/date-utils';
import Skeleton from '@dtvgo/skeleton';

/* Components */
import LiveProgressBar from '../LiveProgressBar';

/* Utils */
import useCurrentSchedule from '../../hooks/useCurrentSchedule';
import { getImage } from '../../utils/imageUtils';
import { TIME_FORMAT_FOR_SCHEDULES } from '../../utils/constants';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';
import { useSubtitleCardStationWithSchedule } from './utils';
import { useTitle } from '../../utils/metaDataUtils';
import useFetchContent from '../../hooks/useFetchContent';

/* Styles */
import './CardStationWithSchedule.scss';

const IMG_WIDTH = 80;

function CardStationWithSchedule({
  className,
  item: station,
  focused,
  onMouseDown,
  onMouseEnter,
}) {
  const { schedule, isCompleted } = useCurrentSchedule(station);
  const { content } = useFetchContent({
    contentId: schedule?.content,
    sourceProvider: schedule?.sourceProvider,
  });
  const { startTime, endTime } = schedule || {};
  const { images } = station;
  const { timeZone } = useIntl();

  const imageUrl = useMemo(() => getImage(
    images,
    IMG_WIDTH,
  ), [images]);

  const title = useTitle(content);
  const subtitle = useSubtitleCardStationWithSchedule(content);

  const interval = useMemo(
    () => {
      if (startTime && endTime) {
        return `${formatDate(startTime, TIME_FORMAT_FOR_SCHEDULES, timeZone)} - ${formatDate(endTime, TIME_FORMAT_FOR_SCHEDULES, timeZone)}`;
      }
      return '';
    },
    [endTime, startTime, timeZone],
  );

  const showLockIcon = useLockIcon(station);
  const isContentBlockedByParentalControl = parentalControlBlock(content);
  const skeleton = <Skeleton className="dtv-html5-c-card-station-with-schedule__skeleton">Loading...</Skeleton>;

  return (
    <CommonCardStationWithSchedule
      className={`dtv-html5-c-card-station-with-schedule ${
        focused ? 'dtv-html5-c-card-station-with-schedule--focused' : ''
      } ${className}`}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <div className="dtv-html5-c-card-station-with-schedule__station">
        <div className="dtv-html5-c-card-station-with-schedule__station-data">
          <CommonCardStationWithSchedule.Title>
            {isCompleted ? (title || <FormattedMessage id="schedule.missing.metadata" />) : skeleton}
          </CommonCardStationWithSchedule.Title>
          <CommonCardStationWithSchedule.SubTitle>
            {isCompleted ? subtitle : skeleton}
          </CommonCardStationWithSchedule.SubTitle>
        </div>
        <div className="dtv-html5-c-card-station-with-schedule__image-container">
          <CommonCardStationWithSchedule.Image
            className={`dtv-html5-c-card-station-with-schedule__image ${showLockIcon ? 'dtv-html5-c-card-station-with-schedule__image_lock' : ''}
              ${(isContentBlockedByParentalControl && !showLockIcon) ? 'dtv-html5-c-card-station-with-schedule__image--block-content' : ''}`}
            imageUrl={imageUrl}
            imageAlt={station?.title}
          />
          {showLockIcon && <CommonCardStationWithSchedule.Lock className="dtv-html5-c-card-station-with-schedule__lock" />}
          {(isContentBlockedByParentalControl && !showLockIcon) && (
            <div className="dtv-html5-c-card-station-with-schedule__parental-control-block">
              <Icon name="parental-block" className="dtv-html5-c-card-station-with-schedule__parental-control-icon" />
            </div>
          )}
        </div>
      </div>
      <div className="dtv-html5-c-card-station-with-schedule__schedule">
        <div className="dtv-html5-c-card-station-with-schedule__duration">
          <CommonCardStationWithSchedule.Duration>
            {isCompleted ? interval : skeleton}
          </CommonCardStationWithSchedule.Duration>
        </div>
        <div className="dtv-html5-c-card-station-with-schedule__progress-bar">
          <LiveProgressBar
            className="dtv-html5-c-card-station-with-schedule__progress"
            startDate={startTime}
            endDate={endTime}
            focused={focused}
          />
        </div>
      </div>
    </CommonCardStationWithSchedule>
  );
}

CardStationWithSchedule.propTypes = {
  className: PropTypes.string,
  item: PropTypes.shape({
    channelId: PropTypes.string,
    title: PropTypes.string,
    sourceProvider: PropTypes.string,
    images: PropTypes.instanceOf(Map),
  }),
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

CardStationWithSchedule.defaultProps = {
  className: '',
  item: undefined,
  onMouseDown: undefined,
  onMouseEnter: undefined,
};

export default memo(withInteractiveFocusable()(memo(CardStationWithSchedule)));
